import { useEffect, useRef, useState, lazy, Suspense, type ComponentType } from "react";
import { createPortal } from "react-dom";
import {
  IonApp, IonTabs, IonRouterOutlet, IonTabBar, IonTabButton,
  IonIcon, IonLabel, IonSpinner, IonBadge
} from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Redirect, Route, useHistory } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import {
  homeOutline, carSportOutline, personCircleOutline, handLeftOutline
} from "ionicons/icons";

import { hydrate, useStore, setPendingChainInvite, refreshPunchChains, clearPendingCapture } from "./store";
import { initPush } from "./native/push";
import { maybeRequestReview } from "./native/review";
import { startAnalytics, trackEvent } from "./analytics";
import { api } from "./api";
import { store as nativeStore } from "./native/storage";
import { detectMilestoneCrossing, MILESTONE_COPY, type MilestoneCrossing } from "./punchMilestones";

// Routes are code-split so the initial bundle ships only the shell + the first
// screen's chunk; the rest stream in on navigation. named export -> default shim.
const named = <P extends object>(p: Promise<Record<string, ComponentType<P>>>, key: string) =>
  p.then(m => ({ default: m[key] }));
const Onboard = lazy(() => named(import("./pages/Onboard"), "Onboard"));
const HomePage = lazy(() => named(import("./pages/Home"), "HomePage"));
const DexPage = lazy(() => named(import("./pages/Dex"), "DexPage"));
const ChainPage = lazy(() => named(import("./pages/Chain"), "ChainPage"));
const MePage = lazy(() => named(import("./pages/Me"), "MePage"));
const ChainDetail = lazy(() => named(import("./pages/ChainDetail"), "ChainDetail"));
const CaptureSheet = lazy(() => named(import("./pages/CaptureSheet"), "CaptureSheet"));
const Landing = lazy(() => named(import("./pages/Landing"), "Landing"));

const RouteFallback = () => (
  <div style={{
    height: "100%", display: "flex", alignItems: "center", justifyContent: "center",
    background: "var(--yelo-ink, #0E0F11)"
  }}>
    <IonSpinner name="crescent" />
  </div>
);
const lazyRoute = (Comp: ComponentType) => () => (
  <Suspense fallback={<RouteFallback />}><Comp /></Suspense>
);

// Public, no-auth landing for a shared capture (/c/:id). A shared link must show
// the car to anyone — including logged-out web visitors who would otherwise be
// swallowed by the marketing Landing — and funnel them to the app.
const PublicCapture = ({ id }: { id: string }) => {
  const [cap, setCap] = useState<any | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let on = true;
    api(`/captures/${id}/public`).then(r => { if (on) setCap(r.capture); }).catch(() => { if (on) setErr(true); });
    return () => { on = false; };
  }, [id]);
  return (
    <IonApp>
      <div style={{ minHeight: "100vh", background: "#0E0F11", color: "#F2F3F5", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }}>
        <div className="brand" style={{ fontSize: 30, marginBottom: 18 }}>Y3LO</div>
        {err && <div style={{ opacity: .7 }}>This catch isn’t available.</div>}
        {!err && !cap && <IonSpinner name="crescent" />}
        {cap && (
          <div style={{ width: "100%", maxWidth: 360, background: "#16181B", border: "1px solid #2A2D31", borderRadius: 22, overflow: "hidden" }}>
            {cap.photo && <img src={cap.photo} alt="" style={{ width: "100%", aspectRatio: "4/5", objectFit: "cover", display: "block" }} />}
            <div style={{ padding: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: ".2em", color: cap.is_grail ? "#FFD400" : "#9CA0A8" }}>{cap.is_grail ? "GRAIL" : String(cap.tier ?? "").toUpperCase()}</div>
              <div style={{ fontSize: 24, fontWeight: 900, margin: "6px 0 2px" }}>{cap.name}</div>
              {cap.color && <div style={{ color: "#9CA0A8", fontSize: 13, textTransform: "capitalize" }}>{cap.color}</div>}
              <div style={{ marginTop: 12, fontSize: 20, fontWeight: 900, color: cap.is_grail ? "#FFD400" : "#39A0FF" }}>+{cap.score} <span style={{ fontSize: 11, color: "#9CA0A8", letterSpacing: ".12em" }}>DEX SCORE</span></div>
              {cap.handle && <div style={{ marginTop: 8, fontSize: 13, color: "#9CA0A8" }}>caught by @{cap.handle}</div>}
              <a href="/" style={{ display: "block", marginTop: 18, padding: "13px 0", borderRadius: 14, background: "#FFD400", color: "#0E0F11", fontWeight: 800, fontSize: 15, textDecoration: "none" }}>Get Y3LO — start your dex →</a>
            </div>
          </div>
        )}
      </div>
    </IonApp>
  );
};

// Consumes the 'yelo:deeplink' CustomEvents dispatched by native/push.ts and
// native/index.ts. Must live inside IonReactRouter to reach the history.
const DeepLinks = () => {
  const history = useHistory();
  useEffect(() => {
    const on = (e: Event) => {
      const d = (e as CustomEvent).detail as { kind?: string; value?: string } | undefined;
      if (!d?.kind) return;
      if (d.kind === "chain" && d.value) history.push(`/chain/${d.value}`);
      if (d.kind === "punch_new") history.push(d.value ? `/chain-new/capture?to=${d.value}` : "/chain-new/capture");
    };
    window.addEventListener("yelo:deeplink", on);
    return () => window.removeEventListener("yelo:deeplink", on);
  }, [history]);
  return null;
};

// Contracts the floating dock (theme.css `html.dock-compact`) once the user
// scrolls into content, and re-expands near the top or on scroll-up. Ionic
// scrolls inside each ion-content's shadow root — window/document scroll never
// fires — so we bind a passive listener to every content's scroll element as it
// appears (a MutationObserver picks up newly-routed/cached pages). Binding is
// idempotent (WeakSet); non-visible cached pages simply never emit scroll.
const DockScroll = () => {
  useEffect(() => {
    const root = document.documentElement;
    const lastY = new WeakMap<Element, number>();
    const bound = new WeakSet<Element>();
    let compact = false;
    const set = (v: boolean) => {
      if (v === compact) return;
      compact = v;
      root.classList.toggle("dock-compact", v);
    };
    const onScroll = (e: Event) => {
      const el = e.target as HTMLElement;
      if (!el || typeof el.scrollTop !== "number") return;
      const y = el.scrollTop;
      const prev = lastY.get(el) ?? 0;
      lastY.set(el, y);
      if (y < 32) return set(false);          // always full near the top
      if (y > prev + 6) set(true);            // scrolling down → contract
      else if (y < prev - 6) set(false);      // scrolling up → expand
    };
    let raf = 0;
    const bind = () => {
      raf = 0;
      document.querySelectorAll("ion-content").forEach((c: any) => {
        c.getScrollElement?.().then((sc: HTMLElement | null) => {
          if (sc && !bound.has(sc)) { bound.add(sc); sc.addEventListener("scroll", onScroll, { passive: true }); }
        }).catch(() => {});
      });
    };
    bind();
    const mo = new MutationObserver(() => { if (!raf) raf = requestAnimationFrame(bind); });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { mo.disconnect(); if (raf) cancelAnimationFrame(raf); root.classList.remove("dock-compact"); };
  }, []);
  return null;
};

// Surfaces the result of a capture whose reveal was dismissed mid-grade (the
// reveal's "run in background" affordance) — otherwise the poll dies with the
// unmounted component and the eventual catch/rejection never surfaces. Polls
// with the same cadence/backoff RarityReveal uses so a slow validation doesn't
// get hammered, and toasts once, from wherever the user has since navigated to.
const PendingCaptureWatcher = () => {
  const pending = useStore(s => s.pendingCapture);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!pending) return;
    let alive = true;
    let tries = 0;
    const id = pending.id;
    const timers: ReturnType<typeof setTimeout>[] = [];

    const showToast = (m: string) => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
      setToast(m);
      toastTimer.current = window.setTimeout(() => setToast(null), 4000);
    };

    const tick = async () => {
      if (!alive) return;
      tries++;
      try {
        const r = await api<{ capture: any }>(`/captures/${id}`);
        if (!alive) return;
        const v = r.capture;
        if (v.validation_status !== "pending") {
          if (v.validation_status === "validated") {
            const name = v.display_name || [v.ai_make, v.ai_model].filter(Boolean).join(" ") || "Unknown car";
            const score = v.score_version === "v2" ? (v.dex_score ?? 0) + (v.drive_xp ?? 0) : (v.points ?? 0);
            showToast(v.is_grail
              ? `Grail catch! ${name} — +${score} pts`
              : `${name} landed — ${String(v.tier ?? "common").toUpperCase()} +${score} pts`);
          } else {
            showToast("That one got away — no car found");
          }
          clearPendingCapture(id);
          return; // stop polling
        }
      } catch { /* keep trying */ }
      if (tries < 20) timers.push(setTimeout(tick, Math.min(1000 * Math.pow(1.5, tries - 1), 5000)));
      else clearPendingCapture(id); // give up gracefully — never poll forever
    };
    tick();
    return () => { alive = false; timers.forEach(clearTimeout); };
  }, [pending?.id]);

  if (!toast) return null;
  return createPortal(
    <div
      style={{
        position: "fixed", left: 0, right: 0, bottom: "calc(20px + var(--yelo-tabbar-h, 0px))",
        display: "flex", justifyContent: "center", pointerEvents: "none",
        zIndex: "var(--z-global-toast, 1250)" as any, padding: "0 16px",
      }}
    >
      <div style={{
        pointerEvents: "auto", background: "var(--yelo-ink-3)", color: "var(--yelo-text)",
        border: "1px solid var(--yelo-line)", borderRadius: 12, padding: "10px 16px",
        fontSize: 13, fontWeight: 600, textAlign: "center", boxShadow: "var(--shadow-float, 0 12px 28px rgba(0,0,0,.35))",
      }}>{toast}</div>
    </div>,
    document.body,
  );
};

// Punch key: mate_handle stays stable across a rematch (a new chain gets a new
// id), so it's the identity a pair is tracked under, both here and in the
// persisted floor map below.
const PUNCH_MILESTONE_KEY = "yelo.punchMilestone.v1";

// In-app payoff for crossing a Punch pair-streak milestone (3/7/14/30/60 days)
// — the worker already awards XP + fires a push for this (see the Punch
// section in worker.ts), but a player who's actually in the app at the moment
// of crossing got zero visual reward. Crossing detection is entirely
// client-side: it diffs the punchPairs the app already polls (refreshPunchChains,
// above) against a per-pair "last handled milestone" floor persisted in native
// storage (see punchMilestones.ts for the pure detector + the first-sight /
// rematch-reset / highest-only semantics). Mounted alongside
// PendingCaptureWatcher, so it gets the same gating for free (only rendered
// once authed and past onboarding).
const PunchMilestoneWatcher = () => {
  const pairs = useStore(s => s.punchPairs);
  const [celebration, setCelebration] = useState<MilestoneCrossing | null>(null);
  // null = not hydrated from storage yet — must not run detection against an
  // empty map, or every existing pair would read as "first sight" on launch.
  const floorsRef = useRef<Record<string, number> | null>(null);

  useEffect(() => {
    let on = true;
    nativeStore.get<Record<string, number>>(PUNCH_MILESTONE_KEY).then(v => { if (on) floorsRef.current = v ?? {}; });
    return () => { on = false; };
  }, []);

  useEffect(() => {
    if (floorsRef.current === null || pairs.length === 0) return;
    const { celebration: won, floors } = detectMilestoneCrossing(
      pairs.map(p => ({ pairKey: p.mate_handle, streak_days: p.streak_days, state: p.state })),
      floorsRef.current,
    );
    floorsRef.current = floors;
    nativeStore.set(PUNCH_MILESTONE_KEY, floors);
    if (won) {
      trackEvent("punch_milestone_celebrated", { mate: won.pairKey, milestone: won.milestone });
      if (won.milestone === 3) void maybeRequestReview("punch_streak");
      setCelebration(won);
    }
  }, [pairs]);

  if (!celebration) return null;
  const copy = MILESTONE_COPY[celebration.milestone];
  const dismiss = () => setCelebration(null);
  return createPortal(
    <div
      style={{
        position: "fixed", inset: 0, zIndex: "var(--z-celebrate, 1300)" as any,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: 28, textAlign: "center",
        background: "radial-gradient(620px 440px at 50% 40%, rgba(255,212,0,.20), rgba(8,9,11,.95) 64%)",
        animation: "pmc-fade .4s ease",
      }}
      onClick={dismiss}
    >
      <style>{`
        @keyframes pmc-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes pmc-pop { 0% { transform: scale(.82); opacity: 0 } 100% { transform: scale(1); opacity: 1 } }
      `}</style>
      <div onClick={e => e.stopPropagation()} style={{ animation: "pmc-pop .35s var(--ease-out-quint, ease-out)" }}>
        <div aria-hidden style={{ fontSize: 42, lineHeight: 1 }}>🥊</div>
        <div style={{
          fontFamily: "var(--yelo-display-font)", fontWeight: 700, letterSpacing: ".02em",
          fontSize: copy.size, color: "var(--yelo-yellow)", marginTop: 10,
        }}>
          {celebration.milestone}-DAY STREAK
        </div>
        <div style={{ fontSize: 15, color: "var(--yelo-text)", maxWidth: 270, margin: "10px auto 0", lineHeight: 1.45 }}>
          you and @{celebration.pairKey} have punched {celebration.milestone} days straight
        </div>
        <div style={{ fontSize: 13.5, color: "var(--yelo-text-dim)", marginTop: 6, maxWidth: 260, marginLeft: "auto", marginRight: "auto" }}>
          {copy.tagline}
        </div>
        <div><button className="btn-primary" style={{ marginTop: 20, maxWidth: 240 }} onClick={dismiss}>keep it alive</button></div>
      </div>
    </div>,
    document.body,
  );
};

export const App = () => {
  const ready = useStore(s => s.ready);
  const user = useStore(s => s.user);
  const pendingRecoveryCode = useStore(s => s.pendingRecoveryCode);
  const pendingInvite = useStore(s => s.pendingInvite);
  useEffect(() => { hydrate(); startAnalytics(); }, []);

  // Capture a /chain/:id?invite=CODE deep link before anything (onboarding,
  // redirects) can rewrite the URL. Redemption happens later, once authed —
  // see ChainPage / ChainDetail. Stored persistently so it survives signup.
  useEffect(() => {
    if (typeof location === "undefined") return;
    const m = location.pathname.match(/\/chain\/([A-Za-z0-9_-]+)/);
    const code = new URLSearchParams(location.search).get("invite");
    if (m && code) setPendingChainInvite(m[1], code);
  }, []);

  // Hide the native splash only after React's ready — splash is configured with
  // launchAutoHide:false so it stays up through the JS/Ionic boot, killing the
  // white-flash gap between native splash and first paint.
  useEffect(() => {
    if (!ready) return;
    if (!Capacitor.isNativePlatform()) return;
    SplashScreen.hide({ fadeOutDuration: 250 }).catch(() => {});
  }, [ready]);

  // Push registration is deferred until the user is authenticated AND past
  // the recovery-code / invite gates — asking iOS for permission before
  // the user understands what we are is a guaranteed deny.
  useEffect(() => {
    if (!user || pendingRecoveryCode || pendingInvite) return;
    initPush();
  }, [user, pendingRecoveryCode, pendingInvite]);

  // Punch attention state: badge + Home strip. 60s poll + foreground refresh.
  useEffect(() => {
    if (!user || pendingRecoveryCode || pendingInvite) return;
    refreshPunchChains();
    const t = setInterval(refreshPunchChains, 60000);
    const onVisible = () => { if (document.visibilityState === "visible") refreshPunchChains(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVisible); };
  }, [user, pendingRecoveryCode, pendingInvite]);
  const punchChains = useStore(s => s.punchChains);
  const yourMove = punchChains.filter(r => r.my_turn).length;
  const anyDying = punchChains.some(r => r.my_turn && Number(r.last_expires_at ?? 0) - Date.now() < 3 * 3600 * 1000);

  // The website is now pure marketing for the unauthenticated: ANY anonymous web
  // visitor (every path) sees the Landing + app-store CTAs — there is no web
  // signup. Users who already hold a session keep the full web app. Native
  // shells (Capacitor) never see the Landing. New users are funnelled to the
  // App Store / Play Store from the Landing.
  const isWeb = !Capacitor.isNativePlatform();
  const showLanding = isWeb && ready && !user;

  if (!ready) {
    return (
      <IonApp>
        <div style={{
          height: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
          background: "#0E0F11"
        }}>
          <div className="brand" style={{ fontSize: 64 }}>Y3LO</div>
        </div>
      </IonApp>
    );
  }

  // A shared capture link (/c/:id) renders a public read-only card for ANYONE —
  // authed or not, web or native — so the share is never swallowed by Landing.
  const capMatch = typeof location !== "undefined" ? location.pathname.match(/^\/c\/([A-Za-z0-9_-]+)$/) : null;
  if (capMatch) return <PublicCapture id={capMatch[1]} />;

  if (showLanding) return <Suspense fallback={<RouteFallback />}><Landing /></Suspense>;

  // basename follows the build base (root "/" today → undefined; supports a sub-path deploy if BASE_URL changes)
  const basename = (import.meta as any).env?.BASE_URL?.replace(/\/$/, "") || undefined;

  // Single persistent IonReactRouter — switching the children based on auth
  // state inside one router keeps Ionic's history/lifecycle warm across the
  // onboarding -> tabs transition. The old code mounted a fresh IonReactRouter
  // for each branch, which left IonRouterOutlet blank after the invite step.
  const needsOnboard = !user || pendingRecoveryCode || pendingInvite;

  return (
    <IonApp>
      <IonReactRouter basename={basename}>
        {needsOnboard ? (
          <Suspense fallback={<RouteFallback />}><Onboard /></Suspense>
        ) : (
          <>
          <DeepLinks />
          <DockScroll />
          <PendingCaptureWatcher />
          <PunchMilestoneWatcher />
          <IonTabs>
            <IonRouterOutlet>
              <Route exact path="/home" render={lazyRoute(HomePage)} />
              <Route exact path="/dex" render={lazyRoute(DexPage)} />
              <Route exact path="/chain" render={lazyRoute(ChainPage)} />
              <Route exact path="/chain/:id" render={lazyRoute(ChainDetail)} />
              <Route exact path="/chain/:id/capture" render={lazyRoute(CaptureSheet)} />
              <Route exact path="/chain-new/capture" render={lazyRoute(CaptureSheet)} />
              <Route exact path="/me" render={lazyRoute(MePage)} />
              <Route exact path="/"><Redirect to="/home" /></Route>
            </IonRouterOutlet>

            {/* Four-tab IA: Home is the do-now (quests + snap), Dex is the
                browsable collection (garage/sets/feed/ranks), Punch is the
                social headline (yellow-car chain game — reached directly via
                its own tab + your-move badge), Me is the profile. */}
            <IonTabBar slot="bottom">
              <IonTabButton tab="home" href="/home">
                <IonIcon icon={homeOutline} />
                <IonLabel>Home</IonLabel>
              </IonTabButton>
              <IonTabButton tab="dex" href="/dex">
                <IonIcon icon={carSportOutline} />
                <IonLabel>Dex</IonLabel>
              </IonTabButton>
              <IonTabButton tab="punch" href="/chain">
                <IonIcon icon={handLeftOutline} />
                <IonLabel>Punch</IonLabel>
                {yourMove > 0 && (
                  <IonBadge color={anyDying ? "danger" : "warning"} aria-label={`${yourMove} chains need your move`}>
                    {yourMove}
                  </IonBadge>
                )}
              </IonTabButton>
              <IonTabButton tab="me" href="/me">
                <IonIcon icon={personCircleOutline} />
                <IonLabel>Me</IonLabel>
              </IonTabButton>
            </IonTabBar>
          </IonTabs>
          </>
        )}
      </IonReactRouter>
    </IonApp>
  );
};
